import { useContext, useEffect, useState, useRef, useMemo } from "react";
import { Box, Typography, LinearProgress, Collapse, Chip } from "@mui/material";
import BlurOnIcon from "@mui/icons-material/BlurOn";

import { ChatContext } from "../../contexts/chat/context";
import { NullStateMessage } from "../../components/layout/NullStateMessage";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { ChatStatus } from "../../components/chat/chat_status";
import { InputContainer } from "../../components/chat/input_container";
import { SQLDisplay } from "../../components/chat/sql_display";
import { ChartDisplay } from "../../components/chat/chart_display";
import {
  TableData,
  Message,
  DocSearch,
  SQLQuery,
  ChartGenerated,
  ReasoningStep,
} from "../../contexts/chat/type";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Format timestamp as "H:MM AM/PM, Mon DD" in user's timezone
const formatMessageTimestamp = (timestamp: Date): string => {
  const date = new Date(timestamp);
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();
  return `${hours}:${minutes} ${ampm}, ${month} ${day}`;
};

// DataTable component for rendering structured table data
const DataTable = ({ table }: { table: TableData }) => {
  return (
    <Box sx={{ my: 2 }}>
      {table.name && (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {table.name}
        </Typography>
      )}
      <Box sx={{ overflowX: "auto" }}>
        <table
          style={{
            borderCollapse: "collapse",
            width: "100%",
            minWidth: "max-content",
            fontSize: "13px",
          }}
        >
          <thead>
            <tr>
              {table.headers.map((header, i) => (
                <th
                  key={i}
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px",
                    backgroundColor: "#f5f5f5",
                    whiteSpace: "nowrap",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td
                    key={j}
                    style={{
                      border: "1px solid #ddd",
                      padding: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      {table.summary && (
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
          {table.summary}
        </Typography>
      )}
    </Box>
  );
};

// Custom animated waiting component (fallback when not streaming)
const CustomAwaitingResponse = () => {
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev === 3 ? 1 : prev + 1));
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {Array.from({ length: dotCount }).map((_, index) => (
        <Box
          key={index}
          sx={{
            backgroundColor: "grey.500",
            height: "8px",
            width: "8px",
            borderRadius: "50%",
            mr: 0.25,
          }}
        />
      ))}
    </Box>
  );
};

// SQL Query Card - expandable with results table
const SqlQueryCard = ({ query }: { query: SQLQuery }) => {
  const [showResults, setShowResults] = useState(false);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: "success.dark", mb: 0.5 }}
      >
        SQL Query (Step {query.iteration}) - {query.row_count} rows
      </Typography>
      <Box
        sx={{
          backgroundColor: "grey.900",
          p: 1.5,
          borderRadius: 1,
          overflow: "auto",
          maxHeight: 120,
        }}
      >
        <Typography
          component="pre"
          sx={{
            fontSize: "0.8rem",
            fontFamily: "monospace",
            color: "grey.100",
            m: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {query.sql || "SQL not available"}
        </Typography>
      </Box>
      {/* Expandable results table */}
      {query.results &&
        query.results.length > 0 &&
        query.columns &&
        query.columns.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: "success.main",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => setShowResults(!showResults)}
            >
              {showResults ? "▼ Hide" : "▶ View"} Results (
              {query.results.length} of {query.row_count} rows)
            </Typography>
            <Collapse in={showResults}>
              <Box
                sx={{
                  mt: 1,
                  maxHeight: 200,
                  overflow: "auto",
                  backgroundColor: "grey.50",
                  borderRadius: 1,
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "0.75rem",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: "#e8e8e8",
                        position: "sticky",
                        top: 0,
                      }}
                    >
                      {query.columns.map((col, idx) => (
                        <th
                          key={idx}
                          style={{
                            padding: "6px 8px",
                            textAlign: "left",
                            borderBottom: "1px solid #ddd",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {query.results.slice(0, 20).map((row, rowIdx) => (
                      <tr
                        key={rowIdx}
                        style={{ borderBottom: "1px solid #eee" }}
                      >
                        {query.columns!.map((col, colIdx) => (
                          <td
                            key={colIdx}
                            style={{ padding: "4px 8px", whiteSpace: "nowrap" }}
                          >
                            {row[col] !== null && row[col] !== undefined ? (
                              String(row[col])
                            ) : (
                              <span
                                style={{ color: "#999", fontStyle: "italic" }}
                              >
                                null
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Collapse>
          </Box>
        )}
    </Box>
  );
};

// Doc Search Card - expandable with documentation response
const DocSearchCard = ({ search }: { search: DocSearch }) => {
  const [showDocs, setShowDocs] = useState(false);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: "secondary.dark", mb: 0.5 }}
      >
        Doc Search (Step {search.iteration})
      </Typography>
      <Box sx={{ backgroundColor: "grey.100", p: 1, borderRadius: 1, mb: 0.5 }}>
        <Typography
          variant="body2"
          sx={{ color: "text.primary", fontStyle: "italic" }}
        >
          "{search.question}"
        </Typography>
      </Box>
      {/* Table names found */}
      {search.table_names && search.table_names.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
          {search.table_names.map((table, tableIdx) => (
            <Box
              key={tableIdx}
              sx={{
                backgroundColor: "white",
                border: "1px solid",
                borderColor: "secondary.main",
                color: "secondary.dark",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.75rem",
                fontFamily: "monospace",
                fontWeight: 500,
              }}
            >
              {table}
            </Box>
          ))}
        </Box>
      )}
      {/* Expandable documentation response */}
      {search.schema_info && (
        <Box sx={{ mt: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: "secondary.main",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => setShowDocs(!showDocs)}
          >
            {showDocs ? "▼ Hide" : "▶ View"} Documentation Response
          </Typography>
          <Collapse in={showDocs}>
            <Box
              sx={{
                mt: 1,
                p: 1,
                backgroundColor: "grey.50",
                borderRadius: 1,
                maxHeight: 200,
                overflow: "auto",
              }}
            >
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  m: 0,
                  fontSize: "0.75rem",
                }}
              >
                {search.schema_info}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

// Unified AgentTrace component - shows during streaming (expanded) and after completion (collapsed)
interface AgentTraceProps {
  // For streaming mode - uses context
  isStreaming?: boolean;
  // For completed message mode - uses message data
  message?: Message;
}

const AgentTraceDisplay = ({
  isStreaming = false,
  message,
}: AgentTraceProps) => {
  const { agentState } = useContext(ChatContext);

  // Auto-expand during streaming, collapsed for completed messages
  const [showDetails, setShowDetails] = useState(isStreaming);

  // Auto-expand when streaming starts, auto-collapse when it ends
  useEffect(() => {
    if (isStreaming) {
      setShowDetails(true);
    }
  }, [isStreaming]);

  // Get data from either streaming state or completed message
  const sqlQueries: SQLQuery[] = isStreaming
    ? agentState.sqlQueries
    : message?.sqlQueries || [];
  const docSearches: DocSearch[] = isStreaming
    ? agentState.docSearches
    : message?.docSearches || [];
  const charts: ChartGenerated[] = isStreaming
    ? agentState.charts
    : message?.charts || [];
  const reasoningHistory: ReasoningStep[] = isStreaming
    ? agentState.reasoningHistory
    : message?.reasoningHistory || [];
  const currentIteration = isStreaming
    ? agentState.currentIteration
    : message?.iterationCount || 0;
  const maxIterations = isStreaming
    ? agentState.maxIterations
    : message?.iterationCount || 0;
  const currentReasoning = isStreaming ? agentState.reasoning : "";

  // Combine and sort all trace events by iteration number
  type TraceEvent =
    | { type: "sql"; iteration: number; data: SQLQuery }
    | { type: "doc"; iteration: number; data: DocSearch };

  const sortedTraceEvents: TraceEvent[] = useMemo(() => {
    const events: TraceEvent[] = [
      ...sqlQueries.map(
        (q): TraceEvent => ({ type: "sql", iteration: q.iteration, data: q })
      ),
      ...docSearches.map(
        (d): TraceEvent => ({ type: "doc", iteration: d.iteration, data: d })
      ),
    ];
    return events.sort((a, b) => a.iteration - b.iteration);
  }, [sqlQueries, docSearches]);

  // Check if there's any trace data to show
  const hasTraceData =
    sqlQueries.length > 0 ||
    docSearches.length > 0 ||
    charts.length > 0 ||
    reasoningHistory.length > 0;

  // For completed messages, only show if there's trace data
  if (!isStreaming && !hasTraceData) {
    return null;
  }

  const progress =
    maxIterations > 0 ? (currentIteration / maxIterations) * 100 : 0;

  // Wrapper - full box for streaming, inline section for completed
  const content = (
    <>
      {/* Header - different for streaming vs completed */}
      {isStreaming ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "primary.main",
                  animation: "pulse 1.5s infinite",
                  "@keyframes pulse": {
                    "0%": { opacity: 1, transform: "scale(1)" },
                    "50%": { opacity: 0.5, transform: "scale(1.2)" },
                    "100%": { opacity: 1, transform: "scale(1)" },
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "primary.main" }}
              >
                Thinking...
              </Typography>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mb: 1.5,
              height: 4,
              borderRadius: 2,
              backgroundColor: "grey.200",
            }}
          />
        </>
      ) : (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            mb: showDetails ? 1 : 0,
            backgroundColor: "grey.100",
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "grey.300",
            "&:hover": {
              backgroundColor: "grey.200",
              borderColor: "grey.400",
            },
          }}
          onClick={() => setShowDetails(!showDetails)}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {showDetails ? "▼" : "▶"} Agent trace ({currentIteration} steps)
          </Typography>
        </Box>
      )}

      {/* Current reasoning - only during streaming */}
      {isStreaming && currentReasoning && (
        <Box
          sx={{ backgroundColor: "grey.50", p: 2, borderRadius: 1, mb: 1.5 }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.primary",
              fontStyle: "italic",
              fontSize: "0.9rem",
              lineHeight: 1.6,
            }}
          >
            {currentReasoning}
          </Typography>
        </Box>
      )}

      {/* Activity indicators - white background with colored border */}
      {(isStreaming || showDetails) && (
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            mb: hasTraceData ? 1 : 0,
          }}
        >
          {docSearches.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: "white",
                border: "1.5px solid",
                borderColor: "secondary.main",
                color: "secondary.dark",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.75rem",
              }}
            >
              <span>📚</span>
              <span>
                {docSearches.length} doc search
                {docSearches.length > 1 ? "es" : ""}
              </span>
            </Box>
          )}
          {sqlQueries.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: "white",
                border: "1.5px solid",
                borderColor: "success.main",
                color: "success.dark",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.75rem",
              }}
            >
              <span>🔍</span>
              <span>
                {sqlQueries.length} SQL quer
                {sqlQueries.length > 1 ? "ies" : "y"}
              </span>
            </Box>
          )}
          {charts.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: "white",
                border: "1.5px solid",
                borderColor: "warning.main",
                color: "warning.dark",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.75rem",
              }}
            >
              <span>📊</span>
              <span>
                {charts.length} chart{charts.length > 1 ? "s" : ""}
              </span>
            </Box>
          )}
        </Box>
      )}

      {/* Expandable details */}
      {hasTraceData && (
        <Box sx={{ mt: isStreaming ? 1.5 : 0 }}>
          {isStreaming && (
            <Typography
              variant="body2"
              sx={{
                color: "primary.main",
                cursor: "pointer",
                fontWeight: 500,
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? "Hide details" : "Show details"}
            </Typography>
          )}
          <Collapse in={showDetails}>
            <Box
              sx={{
                mt: 1,
                pl: 2,
                borderLeft: "3px solid",
                borderColor: "grey.300",
              }}
            >
              {/* Render all trace events sorted by iteration */}
              {sortedTraceEvents.map((event, idx) => {
                if (event.type === "sql") {
                  return (
                    <SqlQueryCard
                      key={`sql-${event.iteration}-${idx}`}
                      query={event.data}
                    />
                  );
                } else {
                  return (
                    <DocSearchCard
                      key={`doc-${event.iteration}-${idx}`}
                      search={event.data}
                    />
                  );
                }
              })}
            </Box>
          </Collapse>
        </Box>
      )}
    </>
  );

  // For streaming, wrap in a standalone box
  if (isStreaming) {
    return (
      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
        <Box
          sx={{
            maxWidth: "80%",
            minWidth: "300px",
            p: 2,
            borderRadius: "8px",
            borderBottomLeftRadius: 0,
            backgroundColor: "white",
            border: "1px solid",
            borderColor: "primary.light",
          }}
        >
          {content}
        </Box>
      </Box>
    );
  }

  // For completed messages, render inline
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "grey.200" }}>
      {content}
    </Box>
  );
};

// Suggestion prompts for the welcome screen
const SUGGESTION_PROMPTS = [
  "Top selling products",
  "Which SKUs are at risk of stockout?",
  "Chart inventory value over time",
];

// Custom message container component for full-screen chat
const CustomMessageContainer = () => {
  const { messages, isAwaitingResponse, agentState, sendMessage } =
    useContext(ChatContext);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Show suggestion chips only when there's just the welcome message
  const showSuggestions =
    messages.length === 1 &&
    messages[0].type === "received" &&
    !isAwaitingResponse;

  // Auto-scroll to bottom when new messages arrive or agent state changes
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Scroll to bottom whenever messages or agent state changes
  useEffect(() => {
    scrollToBottom();
  }, [messages, agentState]);

  return (
    <>
      {messages.map((message, index) => {
        const isTableMessage = message.dataType === "table" && message.tables;

        return (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent:
                message.type === "sent" ? "flex-end" : "flex-start",
              mb: 1,
            }}
          >
            <Box
              sx={{
                maxWidth: "70%",
                minWidth: "100px",
                p: 2,
                borderRadius:
                  message.type === "received"
                    ? "4px 16px 16px 16px"
                    : "16px 16px 4px 16px",
                backgroundColor:
                  message.type === "sent" ? "#e6eefb" : "#ffffff",
                borderLeft: message.type === "received" ? "3px solid" : "none",
                borderColor:
                  message.type === "received" ? "primary.main" : "transparent",
                boxShadow:
                  message.type === "received"
                    ? "0 2px 8px rgba(0,0,0,0.06)"
                    : "0 1px 3px rgba(0,0,0,0.08)",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {isTableMessage && message.content && (
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  {message.content}
                </Typography>
              )}

              {isTableMessage && message.tables ? (
                <Box sx={{ overflowX: "auto" }}>
                  {message.tables.map((table, idx) => (
                    <DataTable key={idx} table={table} />
                  ))}
                </Box>
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Custom styling for bullet points
                    ul: ({ children }) => (
                      <Box
                        component="ul"
                        sx={{
                          margin: "0.5em 0",
                          paddingLeft: "1.5em",
                        }}
                      >
                        {children}
                      </Box>
                    ),
                    // Custom styling for ordered lists
                    ol: ({ children }) => (
                      <Box
                        component="ol"
                        sx={{
                          margin: "0.5em 0",
                          paddingLeft: "1.5em",
                        }}
                      >
                        {children}
                      </Box>
                    ),
                    // List items with consistent font size
                    li: ({ children }) => (
                      <Typography
                        component="li"
                        variant="body2"
                        sx={{
                          marginBottom: "0.4em",
                          lineHeight: 1.6,
                          "&:last-child": {
                            marginBottom: 0,
                          },
                        }}
                      >
                        {children}
                      </Typography>
                    ),
                    // Better paragraph spacing - same size as list items
                    p: ({ children }) => (
                      <Typography
                        component="p"
                        variant="body2"
                        sx={{
                          margin: "0.5em 0",
                          lineHeight: 1.6,
                          "&:first-of-type": {
                            marginTop: 0,
                          },
                          "&:last-child": {
                            marginBottom: 0,
                          },
                        }}
                      >
                        {children}
                      </Typography>
                    ),
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              )}
              {/* Display SQL queries if present */}
              {message.type === "received" &&
                message.sqlQueries &&
                message.sqlQueries.length > 0 && (
                  <SQLDisplay queries={message.sqlQueries} />
                )}

              {/* Display charts if present */}
              {message.type === "received" &&
                message.charts &&
                message.charts.length > 0 && (
                  <ChartDisplay charts={message.charts} />
                )}

              {/* Display agent trace (inline, collapsed by default) */}
              {message.type === "received" && (
                <AgentTraceDisplay message={message} />
              )}

              <Typography
                variant="caption"
                sx={{ mt: 1, opacity: 0.8, display: "block" }}
              >
                {formatMessageTimestamp(message.timestamp)}
              </Typography>
            </Box>
          </Box>
        );
      })}
      {/* Suggestion chips - show after welcome message */}
      {showSuggestions && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mt: 2,
            mb: 2,
            pl: 1,
          }}
        >
          {SUGGESTION_PROMPTS.map((prompt, index) => (
            <Chip
              key={index}
              label={prompt}
              onClick={() => sendMessage(prompt)}
              variant="outlined"
              sx={{
                cursor: "pointer",
                borderColor: "primary.main",
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.light",
                  borderColor: "primary.dark",
                },
                transition: "all 0.2s ease",
              }}
            />
          ))}
        </Box>
      )}
      {/* Show AgentTraceDisplay when streaming, otherwise show dots if awaiting */}
      {agentState.status === "streaming" ? (
        <AgentTraceDisplay isStreaming />
      ) : (
        isAwaitingResponse && (
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
            <Box
              sx={{
                maxWidth: "70%",
                minWidth: "100px",
                p: 2,
                borderRadius: "8px",
                borderBottomLeftRadius: 0,
                backgroundColor: "grey.100",
                display: "flex",
                alignItems: "center",
              }}
            >
              <CustomAwaitingResponse />
            </Box>
          </Box>
        )
      )}
      <div ref={messagesEndRef} />
    </>
  );
};

export const ChatAssistantContainer = () => {
  const { connectChat, disconnectChat, isConnected, chatSessionId } =
    useContext(ChatContext);
  const { mainRun, lastSuccessfulRun } = useContext(VersionedDataContext);

  // Check if there are any runs available
  const hasAnyRuns = mainRun || lastSuccessfulRun;

  // Auto-start chat session when component mounts and we have a run
  useEffect(() => {
    if (hasAnyRuns && !isConnected) {
      const run = mainRun || lastSuccessfulRun;
      connectChat(run, "onboarding");
    }
  }, [hasAnyRuns, isConnected, mainRun, lastSuccessfulRun, connectChat]);

  // Browser navigation is now handled by the useGuardedNavigation hook

  // Disconnect chat session when component unmounts
  useEffect(() => {
    return () => {
      if (isConnected && chatSessionId) {
        disconnectChat();
      }
    };
  }, [isConnected, chatSessionId, disconnectChat]);

  const mainContent = hasAnyRuns ? (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            p: 1,
          }}
        >
          <CustomMessageContainer />
        </Box>
        <Box
          sx={{
            py: 1,
            display: "flex",
            width: "100%",
            height: "150px",
          }}
        >
          <InputContainer />
        </Box>
      </Box>
    </Box>
  ) : (
    <NullStateMessage>
      Please make a first run before attempting to view run data.
    </NullStateMessage>
  );

  // Show placeholder only in non-development environments
  // In development, show the original chat interface
  if (process.env.REACT_APP_DEPLOYMENT_ID !== "development") {
    // === PLACEHOLDER RETURN STATEMENT (Active in production/staging) ===
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <Box
          sx={{
            maxWidth: "650px",
            textAlign: "left",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: "text.primary",
            }}
          >
            Temporarily Unavailable
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: "text.secondary",
              lineHeight: 1.5,
            }}
          >
            Our AI Chat Assistant is currently turned off while we work on a
            major upgrade behind the scenes. A faster, smarter, and more helpful
            version is on the way soon. 🚀
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 2,
              color: "text.primary",
              fontWeight: 500,
            }}
          >
            If you need anything in the meantime, please reach out to us
            directly:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              alignItems: "flex-start",
              textAlign: "left",
              mb: 3,
            }}
          >
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              • Slack: Your dedicated Atomic support channel
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              • Email: jeremy@atomic.supply
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
            }}
          >
            Thanks for your patience — we're excited to share what's coming
            next!
          </Typography>
        </Box>
      </Box>
    );
  }

  // === ORIGINAL RETURN STATEMENT (Active in development) ===
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 1 }}>
          <Typography variant="h1">Nucleus</Typography>
          <BlurOnIcon sx={{ fontSize: "1.5rem", color: "primary.main" }} />
          <Typography
            variant="caption"
            sx={{
              backgroundColor: "primary.light",
              color: "primary.dark",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontWeight: 500,
              fontSize: "0.7rem",
            }}
          >
            Beta
          </Typography>
        </Box>
        <ChatStatus isConnected={isConnected} />
      </Box>
      <Box
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        {mainContent}
      </Box>
    </Box>
  );
};
